/*
 * LFM theme — token + recipe layer.
 *
 * Three files make up the theme system:
 *   themes.css        → the actual colors, one block per palette (repaint here)
 *   tokens.ts         → this file: names for colors, recipes for common parts
 *   ThemeProvider.tsx → which palette is active, and the JS-side color fixups
 *
 * WHY SPLIT: themes.css answers "what color is it", this file answers "which
 * color does a card border use". Repainting touches only the CSS. Restyling a
 * component touches only the recipe here, and every instance follows.
 *
 * Everything below resolves through CSS variables, so these strings are
 * palette-agnostic — the same `R.card` is correct in every theme.
 *
 * USAGE
 *   import { C, R, cx } from "./theme/tokens";
 *
 *   <div className={R.card}>…</div>
 *   <span className={C.text.muted}>…</span>
 *
 * See ThemeProvider.tsx for mounting a themed subtree.
 *
 * IMPORTANT: these must stay complete literal strings. Tailwind scans source
 * text for class names, so a built-up string like `text-[var(--c-${tone})]`
 * produces no CSS. Always write the whole class.
 */

/* Join class names, dropping falsy branches. */
export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

/*
 * C — raw tokens.
 * Reach for these when a recipe doesn't fit and you're styling one-off.
 */
export const C = {
  bg: {
    page: "bg-[var(--c-bg)]",
    veil: "bg-[var(--c-bg-veil)]",
    surface: "bg-[var(--c-surface)]",
    sunken: "bg-[var(--c-sunken)]",
    sunken2: "bg-[var(--c-sunken-2)]",
    accent: "bg-[var(--c-accent)]",
    accentSoft: "bg-[var(--c-accent-soft)]",
    goodSoft: "bg-[var(--c-good-soft)]",
    warnSoft: "bg-[var(--c-warn-soft)]",
    badSoft: "bg-[var(--c-bad-soft)]",
    squadSoft: "bg-[var(--c-squad-soft)]",
  },
  text: {
    /* 1 → 4, strongest to faintest. */
    base: "text-[var(--c-ink)]",
    secondary: "text-[var(--c-ink-2)]",
    muted: "text-[var(--c-ink-3)]",
    faint: "text-[var(--c-ink-4)]",
    onFill: "text-[var(--c-ink-on-fill)]",
    accent: "text-[var(--c-accent-ink)]",
    good: "text-[var(--c-good-ink)]",
    warn: "text-[var(--c-warn-ink)]",
    bad: "text-[var(--c-bad-ink)]",
    squad: "text-[var(--c-squad-ink)]",
  },
  border: {
    soft: "border-[var(--c-line-soft)]",
    base: "border-[var(--c-line)]",
    strong: "border-[var(--c-line-strong)]",
    accent: "border-[var(--c-accent)]",
    accentSoft: "border-[var(--c-accent-line)]",
    good: "border-[var(--c-good-line)]",
    warn: "border-[var(--c-warn-line)]",
    bad: "border-[var(--c-bad-line)]",
    squad: "border-[var(--c-squad-line)]",
  },
  shadow: {
    sm: "shadow-[var(--c-shadow-sm)]",
    base: "shadow-[var(--c-shadow)]",
    lg: "shadow-[var(--c-shadow-lg)]",
    ringAccent: "shadow-[var(--c-glow-accent)]",
    ringSquad: "shadow-[var(--c-glow-squad)]",
  },
} as const;

/*
 * R — recipes. Whole components, pre-composed.
 * Prefer these over C: they keep spacing and radius consistent too, so the
 * page stays coherent as it grows.
 */
export const R = {
  /* Raised container. The default home for content. */
  card: "rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] shadow-[var(--c-shadow-sm)]",

  /* Card that responds to pointer — pick lists, selectable tiles. */
  cardInteractive:
    "rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] shadow-[var(--c-shadow-sm)] transition-all duration-200 hover:border-[var(--c-line-strong)] hover:shadow-[var(--c-shadow)]",

  /* Selected state for the above. Apply INSTEAD of the hover variant. */
  cardSelected:
    "rounded-xl border border-[var(--c-accent)] bg-[var(--c-accent-soft)] shadow-[var(--c-shadow)] transition-all duration-200",

  /* Recessed region — drop zones, empty slots, tracks. */
  well: "rounded-xl border border-[var(--c-line-soft)] bg-[var(--c-sunken)]",

  /* Dashed placeholder, e.g. an unfilled raid slot. */
  wellEmpty:
    "rounded-lg border border-dashed border-[var(--c-line-strong)] bg-[var(--c-sunken)] text-[var(--c-ink-4)]",

  /* Form controls. */
  input:
    "w-full rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] px-4 py-3.5 text-sm text-[var(--c-ink)] transition-colors focus:border-[var(--c-accent)] focus:outline-none focus:shadow-[var(--c-glow-accent)]",
  inputSm:
    "rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] px-3 py-2 text-sm text-[var(--c-ink)] transition-colors focus:border-[var(--c-accent)] focus:outline-none focus:shadow-[var(--c-glow-accent)]",

  /* Section label — the small uppercase header above a group of fields. */
  label:
    "text-xs font-bold uppercase tracking-widest text-[var(--c-ink-3)]",

  /* Buttons. */
  btnPrimary:
    "rounded-xl bg-[var(--c-accent)] px-5 py-2.5 text-sm font-bold text-[var(--c-ink-on-fill)] shadow-[var(--c-shadow-sm)] transition-all hover:bg-[var(--c-accent-hover)] hover:shadow-[var(--c-shadow)] disabled:cursor-not-allowed disabled:opacity-50",
  btnSecondary:
    "rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] px-5 py-2.5 text-sm font-bold text-[var(--c-ink-2)] transition-all hover:border-[var(--c-line-strong)] hover:bg-[var(--c-sunken)] hover:text-[var(--c-ink)] disabled:cursor-not-allowed disabled:opacity-50",
  btnGhost:
    "rounded-lg px-3 py-1.5 text-sm font-medium text-[var(--c-ink-3)] transition-colors hover:bg-[var(--c-sunken)] hover:text-[var(--c-ink)]",
  btnDanger:
    "rounded-xl border border-[var(--c-bad-line)] bg-[var(--c-bad-soft)] px-5 py-2.5 text-sm font-bold text-[var(--c-bad-ink)] transition-all hover:bg-[var(--c-bad)] hover:text-[var(--c-ink-on-fill)]",

  /* Small status pills. */
  pill: "rounded-full border border-[var(--c-line)] bg-[var(--c-sunken)] px-2.5 py-0.5 text-[11px] font-bold text-[var(--c-ink-2)]",
  pillAccent:
    "rounded-full border border-[var(--c-accent-line)] bg-[var(--c-accent-soft)] px-2.5 py-0.5 text-[11px] font-bold text-[var(--c-accent-ink)]",
  pillGood:
    "rounded-full border border-[var(--c-good-line)] bg-[var(--c-good-soft)] px-2.5 py-0.5 text-[11px] font-bold text-[var(--c-good-ink)]",
  pillWarn:
    "rounded-full border border-[var(--c-warn-line)] bg-[var(--c-warn-soft)] px-2.5 py-0.5 text-[11px] font-bold text-[var(--c-warn-ink)]",
  pillBad:
    "rounded-full border border-[var(--c-bad-line)] bg-[var(--c-bad-soft)] px-2.5 py-0.5 text-[11px] font-bold text-[var(--c-bad-ink)]",

  /* Full-width inline notices. */
  noticeWarn:
    "rounded-xl border border-[var(--c-warn-line)] bg-[var(--c-warn-soft)] p-4 text-[var(--c-warn-ink)]",
  noticeBad:
    "rounded-xl border border-[var(--c-bad-line)] bg-[var(--c-bad-soft)] p-4 text-[var(--c-bad-ink)]",
  noticeGood:
    "rounded-xl border border-[var(--c-good-line)] bg-[var(--c-good-soft)] p-4 text-[var(--c-good-ink)]",

  /* Hairline divider. */
  divider: "border-[var(--c-line-soft)]",
} as const;

/*
 * Colors that are NOT ours to theme.
 *
 * WoW class colors, item rarity and Warcraft Logs parse colors are external
 * conventions — players read them at a glance and recolored versions are
 * simply wrong, not merely different. They stay fixed across every theme.
 * Keep them out of the token set so nobody "tidies" them into it later.
 */
export const FIXED = {
  /* Neutral used when a class/parse is unknown. */
  unknown: "#9d9d9d",
} as const;

/*
 * These two MUST stay in sync with --c-ink / --c-surface in calm.css.
 * CSS variables aren't readable from module scope at import time, and these
 * feed string concatenation (`${color}1f`) that needs a literal 6-digit hex,
 * so the duplication is deliberate. Change one, change the other.
 */
const INK_HEX = "#453a31";
/*
 * The DARKEST surface text realistically lands on (--c-sunken-2), not the
 * lightest. Dark ink loses contrast as the background darkens, so measuring
 * against the deepest well is the worst case — clear it there and the color
 * is legible on every surface in the theme.
 */
const SURFACE_HEX = "#e0d5c5";

type Rgb = [number, number, number]; // channels 0..1

function parseHex(hex: string): Rgb | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((hex || "").trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  return [
    parseInt(h.slice(0, 2), 16) / 255,
    parseInt(h.slice(2, 4), 16) / 255,
    parseInt(h.slice(4, 6), 16) / 255,
  ];
}

function toHex([r, g, b]: Rgb): string {
  const c = (v: number) =>
    Math.round(Math.max(0, Math.min(1, v)) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}

/* WCAG relative luminance. */
function luminance([r, g, b]: Rgb): number {
  const f = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

function contrast(a: Rgb, b: Rgb): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

function hslToRgb(h: number, s: number, l: number): Rgb {
  if (s === 0) return [l, l, l];
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [hue2rgb(p, q, h + 1 / 3), hue2rgb(p, q, h), hue2rgb(p, q, h - 1 / 3)];
}

/*
 * ── onLight() ────────────────────────────────────────────────────────────
 *
 * Blizzard's class and parse colors are picked for a near-black UI. Several
 * are unreadable on a light surface — Priest is literally #FFFFFF, Rogue is
 * pale yellow, Monk is neon mint. Dropping them isn't an option (players
 * identify classes by color), and printing invisible text isn't either.
 *
 * So: hold the hue, and darken only until the color actually clears the
 * contrast target against the background. Hue carries the meaning, so hue is
 * what we protect; lightness is what we spend.
 *
 * Two things this deliberately does NOT do:
 *
 *   1. It does not clamp HSL lightness to a fixed ceiling. Lightness is not
 *      perceptual — at L=42% a yellow still measures ~1.7:1 (invisible) while
 *      a violet measures ~10:1 (needlessly crushed). Only a real luminance
 *      calculation treats every hue correctly, so we drive the WCAG ratio.
 *   2. It does not touch colors that already pass. Death Knight red and
 *      Shaman blue read fine on cream and come back untouched, so the palette
 *      stays as close to canonical as legibility allows.
 *
 * Achromatic inputs (Priest white, unknown grey) have no hue worth keeping,
 * so they resolve to the theme's ink.
 *
 *   target — minimum contrast ratio. 4.5 = WCAG AA for body text.
 *   bgHex  — what the color sits on. Defaults to the darkest surface in the
 *            theme, the worst case for dark ink, so one value is safe
 *            everywhere on the page.
 */
export function onLight(hex: string, target = 4.5, bgHex: string = SURFACE_HEX): string {
  const rgb = parseHex(hex);
  const bg = parseHex(bgHex) ?? parseHex(SURFACE_HEX)!;
  if (!rgb) return INK_HEX;

  // Already legible — leave the canonical color alone.
  if (contrast(rgb, bg) >= target) return toHex(rgb);

  const [r, g, b] = rgb;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;

  // No chroma to preserve — hand it to the theme's ink.
  if (d < 0.02) return INK_HEX;

  const l = (max + min) / 2;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;

  // Walk lightness down and stop at the first value that clears the target,
  // so each color is darkened by the least amount that makes it readable.
  // The check runs on the QUANTIZED hex, not the float candidate: rounding to
  // 8-bit can shave a few hundredths off the ratio and land just under target.
  for (let nl = l; nl >= 0; nl -= 0.005) {
    const hexOut = toHex(hslToRgb(h, s, nl));
    if (contrast(parseHex(hexOut)!, bg) >= target) return hexOut;
  }
  return INK_HEX; // unreachable for light backgrounds; belt and braces.
}

/*
 * Same idea for non-text marks — role bars, class dots, borders. WCAG asks
 * 3:1 for UI components rather than 4.5:1, and holding these to the text
 * target turns every marker into mud.
 */
export function onLightAccent(hex: string, bgHex: string = SURFACE_HEX): string {
  return onLight(hex, 3, bgHex);
}
